import { motion, type Variants, AnimatePresence } from 'framer-motion';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { Dialog, DialogButton, DialogDescription, DialogRoot, DialogTitle } from '~/components/ui/Dialog';
import { db, projectId, deleteByProjectId, getAll, description } from '~/lib/persistence';
import { clearProjectRules } from '~/lib/stores/rules';
import { cubicEasingFn } from '~/utils/easings';
import { json, type LoaderFunction } from '@remix-run/cloudflare';
import { logger } from '~/utils/logger';
import { HistoryItem } from './HistoryItem';
import { binDates } from './date-binning';
import { X, Search, PanelLeftClose, SquarePen, PanelLeftOpen, ChevronRight } from 'lucide-react';
import { useStore } from '@nanostores/react';
import { userStore } from '~/lib/stores/user';
import * as Sentry from '@sentry/remix';
import { planNameCleaner } from '~/lib/utils/planNameCleaner';
import { useDebounce } from '~/hooks/useDebounce';
import { useTranslation } from '~/lib/hooks/useTranslation';
import type { ChatHistoryItem } from '~/services/interfaceService';
import { Loading } from '~/components/chat/Loading';
import { isMobile } from '~/utils/mobile';
import { chatStore } from '@/lib/stores/chat';
import { ProUpgradeModal } from '~/components/ui/ProUpgradeModal';

export const loader: LoaderFunction = async ({ context }) => {
  const baseUrl =
    process.env.NEXT_PUBLIC_GET_ACCESS_URL_DEV ||
    process.env.NEXT_PUBLIC_GET_ACCESS_URL ||
    context.cloudflare.env.NEXT_PUBLIC_GET_ACCESS_URL;

  return json(baseUrl);
};

const menuVariants = {
  closed: {
    opacity: 0,
    visibility: 'hidden',
    left: '-150px',
    transition: {
      duration: 0.2,
      ease: cubicEasingFn,
    },
  },
  open: {
    opacity: 1,
    visibility: 'initial',
    left: 0,
    transition: {
      duration: 0.2,
      ease: cubicEasingFn,
    },
  },
} satisfies Variants;

const categoryVariants = {
  closed: {
    height: 0,
    opacity: 0,
    marginTop: 0,
    transition: {
      duration: 0.3,
      ease: cubicEasingFn,
    },
  },
  open: {
    height: 'auto',
    opacity: 1,
    marginTop: 8,
    transition: {
      duration: 0.3,
      ease: cubicEasingFn,
    },
  },
} satisfies Variants;

type DialogContent = { type: 'delete'; item: ChatHistoryItem } | null;

export function Menu({
  open,
  onClose,
  projects = [],
  type = 'overlay',
  className = '',
  paymentUrl = '',
}: {
  open: boolean;
  onClose?: () => void;
  projects?: ChatHistoryItem[];
  type?: 'overlay' | 'static';
  className?: string;
  paymentUrl?: string;
}) {
  const { t } = useTranslation();
  const menuRef = useRef<HTMLDivElement>(null);
  const [list, setList] = useState<ChatHistoryItem[]>([]);
  const [dialogContent, setDialogContent] = useState<DialogContent>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(new Set());
  const [isProUpgradeModalOpen, setIsProUpgradeModalOpen] = useState(false);

  const toggleCategory = (category: string) => {
    setCollapsedCategories((prev) => {
      const newSet = new Set(prev);

      if (newSet.has(category)) {
        newSet.delete(category);
      } else {
        newSet.add(category);
      }

      return newSet;
    });
  };

  const [isCollapsed, setIsCollapsed] = useState(false);

  const chatStarted = useStore(chatStore).started;

  useEffect(() => {
    if (chatStarted || isMobile()) {
      setIsCollapsed(true);
    }
  }, [chatStarted]);

  const loadEntries = useCallback(async () => {
    if (projects && projects.length > 0) {
      const visibleProjects = projects.filter((project) => {
        if (!project.projectId || !project.description) {
          return false;
        }

        return true;
      });

      console.log('[Menu] menu visibleProjects:', visibleProjects);

      setList(visibleProjects);
    } else if (db) {
      console.log('[Menu] Loading projects from IndexedDB');
    }
  }, [projects]);

  const deleteItem = useCallback(
    (event: React.UIEvent, item: ChatHistoryItem) => {
      event.preventDefault();

      if (db) {
        if (item.projectId) {
          deleteByProjectId(db, item.projectId)
            .then(() => {
              loadEntries();

              if (projectId.get() === item.projectId) {
                // hard page navigation to clear the stores
                window.location.pathname = '/';
              }
            })
            .catch((error) => {
              Sentry.captureException(error, {
                extra: {
                  message: 'Error deleting chat history',
                  item,
                },
              });
              toast.error('Failed to delete conversation');
              logger.error(error);
            });
        }
      } else {
        // If using server projects, we need to implement server-side deletion
        console.warn('[Menu] Server project deletion not yet implemented');
        toast.error('Project deletion not yet implemented for server projects');
      }
    },
    [loadEntries],
  );

  const closeDialog = () => {
    setDialogContent(null);
  };

  const handleLoading = useCallback(() => {
    setIsLoading(true);
  }, []);

  const handleLogoClick = useCallback(() => {
    projectId.set(undefined);
    description.set(undefined);
    clearProjectRules();
  }, []);

  useEffect(() => {
    if (open) {
      loadEntries();
    }
  }, [open, loadEntries]);

  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  const isStatic = type === 'static';

  return (
    <>
      {/* Full-screen loader overlay */}
      {isLoading && <Loading heading="Loading your Project" screen={0} />}

      <motion.div
        ref={menuRef}
        initial={isStatic ? false : 'closed'}
        animate={isStatic ? undefined : open ? 'open' : 'closed'}
        variants={isStatic ? undefined : menuVariants}
        className={
          (isStatic
            ? `flex flex-col ${isCollapsed ? 'w-[68px]' : isMobile() ? 'w-full' : 'w-[280px]'} h-full bg-alpha-background-primary border-r border-alpha-border-dark text-sm relative z-sidebar transition-all duration-300 ${isCollapsed ? 'cursor-pointer' : ''}`
            : 'flex flex-col side-menu fixed top-0 left-0 w-full h-full m-0 rounded-none md:top-[8vh] md:w-[20vw] md:ml-4 md:mt-1 md:h-90vh md:rounded-xl bg-alpha-background-primary p-4 border border-alpha-border-dark z-sidebar shadow-xl shadow-alpha-elements-sidebar-dropdownShadow text-sm') +
          (className ? ` ${className}` : '')
        }
        onClick={() => {
          if (isCollapsed) {
            setIsCollapsed(false);
          }
        }}
      >
        {isCollapsed && isStatic ? (
          <div className="flex flex-col items-center py-6 gap-6 h-full">
            <a
              href="/home"
              className="w-10 h-10 bg-[#1A1A1A] rounded-xl flex items-center justify-center border border-[#2A2A2A] shadow-lg hover:bg-[#222222] transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                handleLogoClick();
              }}
            >
              <img src="/images/dualite-logo.svg" alt="Dualite" className="w-6 h-6" />
            </a>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsCollapsed(false);
              }}
              className="p-2 text-alpha-elements-textTertiary hover:text-alpha-elements-textPrimary transition-colors"
            >
              <PanelLeftOpen size={20} />
            </button>
            <a
              href="/home"
              onClick={(e) => {
                e.stopPropagation();
                handleLogoClick();
              }}
              className="p-2 text-alpha-elements-textTertiary hover:text-alpha-elements-textPrimary transition-colors"
            >
              <SquarePen size={20} />
            </a>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsCollapsed(false);
              }}
              className="p-2 text-alpha-elements-textTertiary hover:text-alpha-elements-textPrimary transition-colors"
            >
              <Search size={20} />
            </button>

            <div className="mt-auto pb-4">
              <UserStatus isCollapsed={true} onOpenUpgrade={() => setIsProUpgradeModalOpen(true)} />
            </div>
          </div>
        ) : (
          <div className="flex-1 flex flex-col h-full w-full overflow-hidden">
            <div className="bg-alpha-background-primary pt-6 px-4">
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-3">
                  <a
                    href="/home"
                    className="w-10 h-10 bg-[#1A1A1A] rounded-xl flex items-center justify-center border border-[#2A2A2A] shadow-lg hover:bg-[#222222] transition-colors"
                    onClick={handleLogoClick}
                  >
                    <img src="/images/dualite-logo.svg" alt="Dualite" className="w-6 h-6" />
                  </a>
                  <span className="text-xl font-bold text-alpha-elements-textPrimary">Dualite</span>
                </div>
                <div className="flex items-center gap-2">
                  {onClose && (
                    <button
                      onClick={onClose}
                      className="md:hidden flex items-center justify-center text-alpha-elements-textTertiary hover:text-alpha-elements-textPrimary transition-colors"
                      aria-label={t('menu.closeMenu')}
                    >
                      <X size={20} />
                    </button>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsCollapsed(true);
                    }}
                    className="block p-2 text-alpha-elements-textTertiary hover:text-alpha-elements-textPrimary transition-colors"
                  >
                    <PanelLeftClose size={20} />
                  </button>
                </div>
              </div>

              <div className="space-y-2 mb-6">
                <a
                  href="/home"
                  onClick={handleLogoClick}
                  className="flex items-center justify-between w-full px-4 py-2.5 bg-[#1A1A1A] hover:bg-[#222222] rounded-xl transition-all border border-[#2A2A2A] group"
                >
                  <span className="text-alpha-elements-textPrimary font-medium">New Chat</span>
                  <div className="flex gap-1 items-center">
                    <kbd className="min-w-[20px] h-5 px-1 flex items-center justify-center bg-[#2A2A2A] rounded text-[10px] text-alpha-elements-textTertiary border border-[#333333] font-sans">
                      ⌘
                    </kbd>
                    <kbd className="min-w-[20px] h-5 px-1 flex items-center justify-center bg-[#2A2A2A] rounded text-[10px] text-alpha-elements-textTertiary border border-[#333333] font-sans">
                      N
                    </kbd>
                  </div>
                </a>

                <div className="relative group">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 text-alpha-elements-textTertiary group-focus-within:text-alpha-elements-textPrimary transition-colors pointer-events-none">
                    <Search size={18} />
                  </div>
                  <input
                    type="text"
                    placeholder="Search"
                    className="w-full pl-11 pr-16 py-2.5 bg-transparent hover:bg-[#1A1A1A] focus:bg-[#1A1A1A] rounded-xl text-alpha-elements-textPrimary placeholder:text-alpha-elements-textTertiary focus:outline-none border border-transparent focus:border-[#2A2A2A] transition-all"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  <div className="absolute right-4 top-1/2 -translate-y-1/2 flex gap-1 items-center pointer-events-none">
                    <kbd className="min-w-[20px] h-5 px-1 flex items-center justify-center bg-[#2A2A2A] rounded text-[10px] text-alpha-elements-textTertiary border border-[#333333] font-sans">
                      ⌘
                    </kbd>
                    <kbd className="min-w-[20px] h-5 px-1 flex items-center justify-center bg-[#2A2A2A] rounded text-[10px] text-alpha-elements-textTertiary border border-[#333333] font-sans">
                      F
                    </kbd>
                  </div>
                </div>
              </div>
            </div>

            <div
              className="flex-1 overflow-y-auto min-h-0 pb-5 px-4"
              style={{
                scrollbarWidth: 'thin',
                scrollbarColor: 'var(--alpha-background-tertiary) var(--alpha-background-primary)',
              }}
            >
              {list.length === 0 && (
                <div className="pl-2 text-alpha-elements-textTertiary">{t('menu.noConversations')}</div>
              )}
              <DialogRoot open={dialogContent !== null}>
                {binDates(list).map(({ category, items }) => {
                  const isCollapsed = collapsedCategories.has(category);
                  const filteredItems = items.filter((item) =>
                    item.description?.toLowerCase().includes(debouncedSearchQuery.toLowerCase()),
                  );

                  if (filteredItems.length === 0) {
                    return null;
                  }

                  return (
                    <div key={category} className="mt-4 first:mt-0">
                      <button
                        onClick={() => toggleCategory(category)}
                        className="flex items-center w-full text-alpha-elements-textPrimary sticky top-0 z-1 pl-2 pt-2 pb-2 text-[13px] bg-alpha-background-primary group transition-colors rounded-md gap-2"
                      >
                        <span className="font-medium shrink-0">{category}</span>
                        <div className="h-[1px] flex-1 bg-alpha-border-dark" />
                        <div className="p-1 rounded bg-[#1A1A1A] border border-[#2A2A2A] text-alpha-elements-textTertiary group-hover:text-alpha-elements-textPrimary transition-colors shrink-0">
                          <motion.div
                            animate={{ rotate: isCollapsed ? 0 : 90 }}
                            transition={{ duration: 0.2, ease: cubicEasingFn }}
                            className="flex items-center justify-center"
                          >
                            <ChevronRight size={14} />
                          </motion.div>
                        </div>
                      </button>
                      <AnimatePresence initial={false}>
                        {!isCollapsed && (
                          <motion.div
                            initial="closed"
                            animate="open"
                            exit="closed"
                            variants={categoryVariants}
                            className="overflow-hidden"
                          >
                            <div className="space-y-2">
                              {filteredItems.map((item) => (
                                <HistoryItem
                                  key={item.projectId}
                                  item={item}
                                  onDelete={() => setDialogContent({ type: 'delete', item })}
                                  onLoading={handleLoading}
                                />
                              ))}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                })}
                <Dialog onBackdrop={closeDialog} onClose={closeDialog}>
                  {dialogContent?.type === 'delete' && (
                    <>
                      <DialogTitle>Delete Chat?</DialogTitle>
                      <DialogDescription asChild>
                        <div>
                          <p>
                            You are about to delete <strong>{dialogContent.item.description}</strong>.
                          </p>
                          <p className="mt-1">Are you sure you want to delete this chat?</p>
                        </div>
                      </DialogDescription>
                      <div className="px-5 pb-4 bg-alpha-elements-background-depth-2 flex gap-2 justify-end">
                        <DialogButton type="secondary" onClick={closeDialog}>
                          Cancel
                        </DialogButton>
                        <DialogButton
                          type="danger"
                          onClick={(event) => {
                            deleteItem(event, dialogContent.item);
                            closeDialog();
                          }}
                        >
                          Delete
                        </DialogButton>
                      </div>
                    </>
                  )}
                </Dialog>
              </DialogRoot>
            </div>
            <div className="flex flex-col border-t border-alpha-border-dark bg-alpha-background-primary z-10 p-4">
              <UserStatus onOpenUpgrade={() => setIsProUpgradeModalOpen(true)} />
            </div>
          </div>
        )}
      </motion.div>

      <ProUpgradeModal
        isOpen={isProUpgradeModalOpen}
        onClose={() => setIsProUpgradeModalOpen(false)}
        isTopUp={planNameCleaner(useStore(userStore).planName).planName === 'Pro'}
        paymentUrl={paymentUrl}
      />
    </>
  );
}

function UserStatus({ isCollapsed = false, onOpenUpgrade }: { isCollapsed?: boolean; onOpenUpgrade: () => void }) {
  const userData = useStore(userStore);

  if (!userData.email) {
    return <div></div>;
  }

  const { planName: cleanedPlanName } = planNameCleaner(userData.planName);
  const isUnlimited = cleanedPlanName.includes('Launch') || cleanedPlanName.includes('Custom');
  const showProgressBar = !isUnlimited;

  if (isCollapsed) {
    return (
      <div className="flex justify-center w-full" onClick={onOpenUpgrade}>
        {userData.photoUrl ? (
          <img
            src={'https://img-wrapper.vercel.app/image?url=' + userData.photoUrl}
            alt="User Avatar"
            className="w-10 h-10 rounded-full object-cover shrink-0 border border-alpha-elements-borderColor"
          />
        ) : (
          <Avatar src={userData.email} />
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full gap-3">
      {showProgressBar && (
        <div className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-2xl p-4">
          <RemainingPrompt
            maxPrompts={userData.maxPrompts}
            promptsUsed={(parseInt(userData.promptsUsed) + parseInt(userData.topUpPromptsUsed)).toString()}
            email={userData.email}
            isUnlimited={isUnlimited}
            topUpPrompts={userData.topUpPrompts}
            planName={cleanedPlanName}
            onOpenUpgrade={onOpenUpgrade}
          />
        </div>
      )}
      <div className="flex items-center justify-between w-full">
        <div className="flex items-center gap-3 min-w-0">
          {userData.photoUrl ? (
            <img
              src={'https://img-wrapper.vercel.app/image?url=' + userData.photoUrl}
              alt="User Avatar"
              className="w-10 h-10 rounded-full object-cover shrink-0"
            />
          ) : (
            <Avatar src={userData.email} />
          )}
          <div className="flex flex-col min-w-0">
            <span className="text-alpha-elements-textPrimary font-semibold truncate text-xs leading-tight pb-0.5">
              {userData.email
                ? userData.email?.length > 25
                  ? userData.email?.slice(0, 25) + '...'
                  : userData.email
                : 'User'}
            </span>
            <span className="text-alpha-elements-textTertiary text-xs font-medium">{cleanedPlanName} Plan</span>
          </div>
        </div>
        <button
          onClick={() => {
            window.location.href = '/settings';
          }}
          className="text-alpha-elements-textTertiary hover:text-alpha-elements-textPrimary transition-colors p-2 rounded-xl bg-alpha-background-secondary border border-alpha-border-dark hover:bg-alpha-background-tertiary"
          title="Settings"
        >
          <div className="i-ph:gear-bold text-lg" />
        </button>
      </div>
    </div>
  );
}

function Avatar({ src }: { src: string }) {
  const avatar = src.split('@')[0].charAt(0).toUpperCase();
  return (
    <div className="w-10 h-10 flex items-center justify-center bg-alpha-background-tertiary rounded-full p-2">
      <span className="text-white text-sm">{avatar}</span>
    </div>
  );
}

function RemainingPrompt({
  maxPrompts,
  promptsUsed,
  email,
  isUnlimited,
  topUpPrompts,
  planName,
  onOpenUpgrade,
}: {
  maxPrompts: string;
  promptsUsed: string;
  email: string;
  isUnlimited: boolean;
  topUpPrompts?: string;
  planName: string;
  onOpenUpgrade: () => void;
}) {
  const used = parseInt(promptsUsed);
  const total = parseInt(maxPrompts) + parseInt(topUpPrompts || '0');
  const percentage = isUnlimited ? 0 : Math.min((used / total) * 100, 100);

  const getBarColor = () => {
    if (percentage >= 90) {
      return 'bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.4)]';
    }

    if (percentage >= 75) {
      return 'bg-yellow-400 shadow-[0_0_8px_rgba(250,204,21,0.4)]';
    }

    return 'bg-green-400 shadow-[0_0_8px_rgba(74,222,128,0.4)]';
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="text-alpha-elements-textPrimary text-md font-bold tracking-tight">
        {isUnlimited
          ? `${promptsUsed} / Unlimited`
          : Number(topUpPrompts) > 0
            ? `${promptsUsed} / ${maxPrompts} + ${topUpPrompts}`
            : `${promptsUsed} / ${maxPrompts}`}{' '}
        <span className="text-alpha-elements-textSecondary text-xs font-medium ml-1">messages</span>
      </div>
      {!isUnlimited && (
        <>
          <div className="h-2 w-full bg-[#1A1A1A] rounded-full overflow-hidden">
            <div
              className={`h-full ${getBarColor()} rounded-full transition-all duration-500`}
              style={{ width: `${percentage}%` }}
            />
          </div>
          {planName.toLowerCase() === 'free' && (
            <button
              onClick={onOpenUpgrade}
              className="mt-2 w-full py-1.5 px-3 bg-white text-black text-xs font-bold rounded-lg hover:bg-gray-200 transition-colors"
            >
              Upgrade
            </button>
          )}
          {planName.includes('Pro') && (
            <button
              onClick={onOpenUpgrade}
              className="mt-2 w-full py-1.5 px-3 bg-white text-black text-xs font-bold rounded-lg hover:bg-gray-200 transition-colors"
            >
              Get more messages
            </button>
          )}
        </>
      )}
    </div>
  );
}
